import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';
import { CircleText, QuestionDisplayMode } from '@doltech/ui/lib/figma/CircleText/CircleText';
import { Box } from '@doltech/ui/lib/figma/Common/Box';
import { QuestionV2 } from '@doltech/ui/lib/figma/Question/QuestionV2';
import { useDeviceDetect } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import { QUESTION_NO_WRAPPER_MARGIN_BOTTOM } from '@doltech/utils/lib/constants';
import cl from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { DoTestSingleAnswerQuestionInstance } from '../student-do-test/model/Question/do-test-single-answer-question.store';
import { useTestSubmissionStore } from '../student-do-test/model/test-submissions.hook';
import { shadows } from '@doltech/ui/lib/figma/shadows';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';

export interface SingleAnswerProps extends React.HTMLAttributes<HTMLDivElement> {
  data: DoTestSingleAnswerQuestionInstance;
}

const CircleTextWrapper = styled.div``;

export const SingleAnswerMain = styled(Box)`
  box-shadow: ${shadows.neutralShadowToBot1};
  padding: 16px 12px;

  .content {
    padding: 0px !important;
  }
  .question-inline {
    margin-left: 8px;
  }

  .question-option-list {
    display: grid;
    gap: 4px;
    margin-top: 12px;
    ${fromScreen(1280)} {
      gap: 4px;
    }
  }
  .question-no-wrapper {
    margin-bottom: ${QUESTION_NO_WRAPPER_MARGIN_BOTTOM}px;
  }
  .question-option-wrapper {
    padding: 4px 8px;
  }

  ${fromScreen(769)} {
    padding: 20px 16px;
    border-radius: 16px;
    border: 1px solid ${colorsV2.neutral20};
  }
`;

const ImageContainer = styled.div`
  margin-bottom: 16px;
  text-align: center;
`;

const Img = styled.img`
  max-width: 100%;
  height: auto;
`;

const selectImageToShow = (image, mobileImage, isMobile) => {
  if (isMobile) {
    return mobileImage || image;
  }
  // Desktop view.
  return image;
};

export const SingleAnswer = observer((props: SingleAnswerProps) => {
  const { data, ...rest } = props;
  const {
    question,
    isImageIncluded,
    image,
    mobileImage,
    options,
    notes,
    formattedQuestionNo,
    onAnswerQuestion,
    setNotes,
    setQuestionProgress,
    userAnswers = [],
  } = data;

  const { isMobile } = useDeviceDetect();
  const store = useTestSubmissionStore();
  return (
    <SingleAnswerMain className="dol-question single-answer" {...rest}>
      <QuestionV2
        no={formattedQuestionNo}
        withDot
        noArrow
        content={question}
        onChangeQuestion={(value) => setQuestionProgress(value)}
      />
      {isImageIncluded && (
        <ImageContainer>
          <Img loading="lazy" src={selectImageToShow(image, mobileImage, isMobile).url} />
        </ImageContainer>
      )}

      <div className="question-option-list">
        {options.map((option, index) => {
          const { key, value } = option;
          const selected = userAnswers && userAnswers.includes(key);
          const note = notes?.find((o) => o.key === key)?.value;
          return (
            <CircleTextWrapper key={key}>
              <CircleText
                note={note}
                onSetNote={(updatedValue) => setNotes?.({ key, value: updatedValue })}
                onClick={() => {
                  if (store.preventUserAction()) return;
                  onAnswerQuestion([option]);
                }}
                className={cl('question-option-wrapper', { selected })}
                circleText=""
                displayMode={QuestionDisplayMode.QUESTION_RADIO}
                selected={selected}
                text={value}
              />
            </CircleTextWrapper>
          );
        })}
      </div>
    </SingleAnswerMain>
  );
});
